import { NextAppointment } from 'infrastructure/classes/next-appointment';
import { useMemo } from 'react';
import { useFormik } from 'formik';
import { ccmNextAppointmentValidationSchema } from 'components/organisms/appointment-details/components/appointment-card/validation-schema';
import { type INextAppointment } from 'infrastructure/interfaces';
import { usePatientNextAppointment } from 'infrastructure/hooks/patient/use-patient-next-appointment';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useTranslation } from 'react-i18next';

interface UseUpdateAppointmentDueDateProps {
  patientGuid?: string;
  nextAppointment?: INextAppointment;
  onSuccess?: () => void;
}

export const useUpdateAppointmentDueDate = ({
  patientGuid,
  nextAppointment,
  onSuccess,
}: UseUpdateAppointmentDueDateProps) => {
  const { t } = useTranslation();
  const { updateLoading, updatePatientNextAppointment } =
    usePatientNextAppointment({
      patientGuid: patientGuid || '',
      enabledFetchingNextAppointment: false,
    });

  const initialData = useMemo(() => {
    if (nextAppointment && patientGuid) {
      return new NextAppointment(patientGuid, nextAppointment);
    }
    return patientGuid ? new NextAppointment(patientGuid) : null;
  }, [nextAppointment, patientGuid]);

  const formik = useFormik<NextAppointment>({
    initialValues: initialData || new NextAppointment(patientGuid || ''),
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!patientGuid) return;

      const scheduledWithData = values.getScheduledWithData();
      await updatePatientNextAppointment({
        patientGuid,
        appointmentDetails: values.appointment
          ? {
              guid: values.appointment.guid,
              ...scheduledWithData,
              appointmentMethod: values.appointment.appointmentMethod,
              scheduledDate: values.prepareDate(
                values.appointment.scheduledDate,
                values.appointment.scheduledTime,
              ),
            }
          : undefined,
      });
      formik.resetForm({ values });

      notificationController.success({
        message: t('messages.updateSuccess', {
          value: t('homeDashboard.updateDueDateModal.successMessageLabel'),
        }),
      });

      if (onSuccess) {
        onSuccess();
      }
    },
    validateOnChange: false,
    validationSchema: ccmNextAppointmentValidationSchema(),
  });

  const { submitForm } = formik;

  return {
    updateLoading,
    formik,
    submitForm,
  };
};
